import React, { useState, useEffect } from 'react';
import { Row, Col, Button, ButtonGroup } from "shards-react";
import { ceil } from 'lodash';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import Select from "react-select";

const Pagination = (props) => {
  const [currentPage, setCurrentPage] = useState(props.page);
  const [localPageSize, setLocalPageSize] = useState(props.pageSize || 25);
  
  let pageSize = props.pageSize || localPageSize;
  let itemCount = props.data?.length || 0;
  let numberOfPages = ceil(itemCount / pageSize) > 0 ? ceil(itemCount / pageSize) : 1;
  
  // For large page sets, we want to show a limited number of page buttons
  const maxButtonsToShow = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxButtonsToShow / 2));
  let endPage = Math.min(numberOfPages, startPage + maxButtonsToShow - 1);
  
  // Adjust the start page if we're near the end
  if (endPage - startPage + 1 < maxButtonsToShow && startPage > 1) {
    startPage = Math.max(1, endPage - maxButtonsToShow + 1);
  }

  useEffect(() => {
    setCurrentPage(props.page);
  }, [props.page]);
  
  useEffect(() => {
    if (props.pageSize) {
      setLocalPageSize(props.pageSize);
    }
  }, [props.pageSize]);

  const changePage = (newPage) => {
    if (newPage > 0 && newPage <= numberOfPages) {
      props.onPageChange(newPage);
    }
  };
  
  const changePageSize = (newSize) => {
    if (props.onPageSizeChange) {
      props.onPageSizeChange(newSize);
      
      // If changing page size would put us beyond the last page, adjust current page
      const newNumberOfPages = ceil(itemCount / newSize);
      if (currentPage > newNumberOfPages) {
        props.onPageChange(newNumberOfPages);
      }
    }
    setLocalPageSize(newSize);
  };
  
  // Calculate display ranges for showing items x-y of z
  const startItem = itemCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(startItem + pageSize - 1, itemCount);

  return (
    <div className="pagination-container" style={{ marginTop: '20px' }}>
      <Row className="align-items-center">
        <Col xs="12" md="6">
          <div className="pagination-info" style={{ fontSize: '13px', color: '#818ea3' }}>
            Showing {startItem} - {endItem} of {itemCount} items
          </div>
        </Col>
        <Col xs="12" md="6">
          <div className="d-flex justify-content-md-end align-items-center mt-3 mt-md-0">
            {props.onPageSizeChange && (
              <div className="page-size-selector mr-3" style={{ width: '250px' }}>
                <Select
                  value={{ value: localPageSize, label: `${localPageSize} items` }}
                  onChange={(option) => changePageSize(option.value)}
                  options={[
                    { value: 10, label: '10 items' },
                    { value: 25, label: '25 items' },
                    { value: 50, label: '50 items' },
                    { value: 100, label: '100 items' }
                  ]}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: state.isFocused ? 'rgba(33, 170, 115, 0.4)' : '#dfe3e9',
                      boxShadow: state.isFocused ? '0 0 0 1px rgba(33, 170, 115, 0.4)' : null,
                      fontSize: '13px',
                      minHeight: '32px',
                      '&:hover': {
                        borderColor: 'rgba(33, 170, 115, 0.4)'
                      }
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: '0 8px'
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected 
                        ? 'rgba(33, 170, 115, 0.1)' 
                        : state.isFocused 
                          ? 'rgba(33, 170, 115, 0.05)' 
                          : null,
                      color: state.isSelected ? '#21AA73' : base.color,
                      fontSize: '13px'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: '#495057'
                    }),
                    indicatorSeparator: () => ({
                      display: 'none'
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      padding: '4px'
                    })
                  }}
                />
              </div>
            )}
            
            <ButtonGroup size="sm">
              <Button 
                theme="light"
                onClick={() => changePage(1)} 
                disabled={currentPage === 1}
                style={{ 
                  padding: '4px 8px',
                  fontSize: '12px',
                  borderColor: '#dfe3e9',
                  color: currentPage === 1 ? '#c1c7d0' : '#495057',
                  backgroundColor: '#fff'
                }}
              >
                <FirstPageIcon fontSize="small" style={{ fontSize: '14px' }} />
              </Button>
              <Button 
                theme="light"
                onClick={() => changePage(currentPage - 1)} 
                disabled={currentPage === 1}
                style={{ 
                  padding: '4px 8px',
                  fontSize: '12px',
                  borderColor: '#dfe3e9',
                  color: currentPage === 1 ? '#c1c7d0' : '#495057',
                  backgroundColor: '#fff'
                }}
              >
                <ArrowBackIosNewIcon fontSize="small" style={{ fontSize: '14px' }} />
              </Button>
              
              {/* Page numbers */}
              {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(pageNum => (
                <Button 
                  key={pageNum}
                  theme={currentPage === pageNum ? "accent" : "light"}
                  onClick={() => changePage(pageNum)} 
                  style={{ 
                    padding: '4px 10px',
                    fontSize: '12px',
                    fontWeight: currentPage === pageNum ? '600' : '400',
                    borderColor: currentPage === pageNum ? '#21AA73' : '#dfe3e9',
                    color: currentPage === pageNum ? '#fff' : '#495057',
                    backgroundColor: currentPage === pageNum ? '#21AA73' : '#fff'
                  }}
                >
                  {pageNum}
                </Button>
              ))}
              
              <Button 
                theme="light"
                onClick={() => changePage(currentPage + 1)} 
                disabled={currentPage === numberOfPages}
                style={{ 
                  padding: '4px 8px',
                  fontSize: '12px',
                  borderColor: '#dfe3e9',
                  color: currentPage === numberOfPages ? '#c1c7d0' : '#495057',
                  backgroundColor: '#fff'
                }}
              >
                <ArrowForwardIosIcon fontSize="small" style={{ fontSize: '14px' }} />
              </Button>
              <Button 
                theme="light"
                onClick={() => changePage(numberOfPages)} 
                disabled={currentPage === numberOfPages}
                style={{ 
                  padding: '4px 8px',
                  fontSize: '12px',
                  borderColor: '#dfe3e9',
                  color: currentPage === numberOfPages ? '#c1c7d0' : '#495057',
                  backgroundColor: '#fff'
                }}
              >
                <LastPageIcon fontSize="small" style={{ fontSize: '14px' }} />
              </Button>
            </ButtonGroup>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Pagination;